import AddButton from "@components/AddButton/AddButton";
import EmptyList from "@components/EmptyList/EmptyList";
import Header from "@components/Header/Header";
import SectionLabel from "@components/SectionLabel/SectionLabel";
import AccountListItem from "@components/AccountListItem/AccountListItem";
import { PALE_BLUE } from "@styles/colors";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
    Container,
    BodyContainer,
    ViewModeContainer,
    EmptyListContainer,
    EmptyListText,
    AddressItemContainer,
    BottomButtonContainer
} from "./ManageAddress.styles";
import { AddressForm } from "./Form";
import { truncateAddress } from "@lib/utils";
import useAddressBookManagement, { Address } from "@hooks/useManageAddressBook";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import SuccessModal from '@components/SuccessModal/SuccessModal';
import { ApiError } from "@lib/api";
import Snackbar from "@components/Snackbar/Snackbar";
import DialogConfirmationBox from "@components/DialogConfirmationBox/DialogConfirmationBox";

const ManageAddress = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const {
        addresses,
        isLoading: isLoadingAddresses,
        addAddress,
        editAddress,
        removeAddress,
        reload,
    } = useAddressBookManagement()

    const [successOpen, setSuccessOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isError, setIsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('');
    const [isLocalLoading, setIsLocalLoading] = useState(false)
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] = useState(false)
    const [toBeRemoved, setToBeRemoved] = useState('')

    const queryParams = new URLSearchParams(location.search);
    const rawMode = queryParams.get('mode');
    const initialMode = rawMode === 'view' || rawMode === 'edit' || rawMode === 'add' ? rawMode : 'view';
    const source = queryParams.get('source');

    const [mode, setMode] = useState<'view' | 'edit' | 'add'>(initialMode);
    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

    const onBackClick = async () => {
        if (source === 'p2p-transfer') {
            navigate('/p2p-transfer');
            return;
        }
        if (mode === 'edit' || mode === 'add') {
            await reload()
            setMode('view');
            setSelectedAddress(null);
            setIsError(false)
            setErrorMessage('')
            // Clear scanned address from location state
            navigate(location.pathname + location.search, { replace: true, state: {} });
        } else {
            navigate('/bank-and-address')
        }
    };

    const handleAddressClick = (address: Address) => {
        setSelectedAddress(address);
        setMode('edit');
    };

    const handleAddNewClick = () => {
        setSelectedAddress(null);
        setMode('add');
    };

    const handlePromptConfirmation = async (uuid: string) => {
        setIsConfirmationDialogVisible(true)
        setToBeRemoved(uuid)
    }

    const handleHideConfirmation = async () => {
        setIsConfirmationDialogVisible(false)
    }

    const handleAddAddressOperation = async (
        name: string,
        address: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.addedSuccessfully'));
        try {
            await addAddress({
                name,
                address,
            })
            setSuccessOpen(true);
            setTimeout(async () => {
                onBackClick()
            }, 2000)
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific address restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.addressDisabled'))
                    return
                }

                if (!error.errorCode || error.errorCode === 500) {
                    try {
                        const errorObj = JSON.parse(error.message.replace('Back-end error 400 ', ''))
                        const errorMessage = errorObj.error?.message || errorObj.message || error.message
                        setErrorMessage(errorMessage)
                    } catch {
                        setErrorMessage(error.message)
                    }
                } else {
                    const translationKey = `errors.${error.errorCode}.description`
                    const translatedMessage = t(translationKey)
                    const errorMessage = translatedMessage !== translationKey
                        ? translatedMessage
                        : t('errors.genericServerDescription')
                    setErrorMessage(errorMessage)
                }
                setIsError(true)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleEditAddressOperation = async (
        name: string,
        address: string,
        uuid: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.updatedSuccessfully'));
        try {
            await editAddress({
                uuid,
                name,
                address,
            })
            setSuccessOpen(true);
            setTimeout(async () => {
                onBackClick()
            }, 2000)
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific address restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.addressDisabled'))
                    return
                }

                if (!error.errorCode || error.errorCode === 500) {
                    try {
                        const errorObj = JSON.parse(error.message.replace('Back-end error 400 ', ''))
                        const errorMessage = errorObj.error?.message || errorObj.message || error.message
                        setErrorMessage(errorMessage)
                    } catch {
                        setErrorMessage(error.message)
                    }
                } else {
                    const translationKey = `errors.${error.errorCode}.description`
                    const translatedMessage = t(translationKey)
                    const errorMessage = translatedMessage !== translationKey
                        ? translatedMessage
                        : t('errors.genericServerDescription')
                    setErrorMessage(errorMessage)
                }
                setIsError(true)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleRemoveAddressOperation = async (uuid: string) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.removedSuccessfully'));
        try {
            await removeAddress({ uuid })
            setSuccessOpen(true);

            setTimeout(async () => {
                onBackClick()
            }, 2000)
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                if (!error.errorCode || error.errorCode === 500) {
                    try {
                        const errorObj = JSON.parse(error.message.replace('Back-end error 400 ', ''))
                        const errorMessage = errorObj.error?.message || errorObj.message || error.message
                        setErrorMessage(errorMessage)
                    } catch {
                        setErrorMessage(error.message)
                    }
                } else {
                    const translationKey = `errors.${error.errorCode}.description`
                    const translatedMessage = t(translationKey)
                    const errorMessage = translatedMessage !== translationKey
                        ? translatedMessage
                        : t('errors.genericServerDescription')
                    setErrorMessage(errorMessage)
                }
                setIsError(true)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleSave = async (params: any) => {
        if (source === 'p2p-transfer') {
            await handleAddAddressOperation(params.name, params.address)
        } else {
            if (selectedAddress) {
                await handleEditAddressOperation(params.name, params.address, params.uuid)
            } else {
                await handleAddAddressOperation(params.name, params.address)
            }
        }
    };

    const handleRemove = async (uuid: string) => {
        await handleRemoveAddressOperation(uuid)
    };

    return (
        <Container>
            <Header title={t('bankAndAddress.address.header')} showBackButton onBackClick={async () => await onBackClick()} />
            {isLoadingAddresses && <LoadingModal visible={isLoadingAddresses} />}
            {isLocalLoading && <LoadingModal visible={isLocalLoading} />}
            <BodyContainer>
                {mode === 'view' && <ViewModeContainer>
                    <SectionLabel>{t('bankAndAddress.address.sectionLabel')}</SectionLabel>
                    {addresses && !addresses.length &&
                        <EmptyListContainer>
                            <EmptyList>
                                <EmptyListText>
                                    {t('bankAndAddress.address.emptyText')}
                                </EmptyListText>
                                <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.address.addNew')} $backgroundColor={PALE_BLUE} />
                            </EmptyList>
                        </EmptyListContainer>}
                    {
                        addresses &&
                        addresses.length > 0 && (
                            addresses.map((address, index) => (
                                <AddressItemContainer key={index}>
                                    <AccountListItem
                                        oneLine
                                        name={address.name}
                                        accountNumber={truncateAddress(address.address, 8, 8)}
                                        profilePicturePlaceholder={address.name}
                                        iconUrl=""
                                        provider={t('bankAndAddress.address.provider')}
                                        isValid={true}
                                        tag="edit"
                                        onClick={() => handleAddressClick(address)}
                                    />
                                </AddressItemContainer>
                            ))
                        )}
                    {addresses && addresses.length !== 0 && <BottomButtonContainer>
                        <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.address.addNew')} $backgroundColor={PALE_BLUE} />
                    </BottomButtonContainer>}
                </ViewModeContainer>}
                {mode === 'edit' &&
                    <AddressForm
                        onSave={handleSave}
                        onRemove={handlePromptConfirmation}
                        initialName={selectedAddress?.name}
                        initialAddress={selectedAddress?.address}
                        uuid={selectedAddress?.address_uuid}
                        errorText={''}
                        mode="edit"
                    />
                }
                {mode === 'add' &&
                    <AddressForm
                        onSave={handleSave}
                        onRemove={handlePromptConfirmation}
                        initialName={selectedAddress?.name}
                        initialAddress={selectedAddress?.address}
                        uuid={selectedAddress?.address_uuid}
                        errorText={''}
                        mode="add"
                    />
                }
            </BodyContainer>
            {successOpen && <SuccessModal
                visible={successOpen}
                message={successMessage}
                onHide={() => setSuccessOpen(false)}
            />}
            {isError && <Snackbar
                width='90%'
                duration={8000}
                message={errorMessage}
                isVisible={isError}
                onClose={() => {
                    setIsError(false)
                    setErrorMessage('')
                }}
            />}
            {isConfirmationDialogVisible && <DialogConfirmationBox
                isOpen={isConfirmationDialogVisible}
                title={t('bankAndAddress.address.removeConfirmation.title')}
                description={t('bankAndAddress.address.removeConfirmation.description')}
                positiveButtonText={t('bankAndAddress.address.removeConfirmation.positiveButtonText')}
                negativeButtonText={t('bankAndAddress.address.removeConfirmation.negativeButtonText')}
                onPositiveClick={handleHideConfirmation}
                onNegativeClick={async () => {
                    await handleHideConfirmation()
                    await handleRemove(toBeRemoved)
                }}
                onOverlayClick={handleHideConfirmation}
            />}
        </Container>
    )
}

export default ManageAddress;